import React from "react";
import { useAuth } from "../../../context/AuthContext";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { participantApi } from "../../../api/participant";
import { ScaleLoader } from "react-spinners";

const getCollection = (response: any) => response?.data || response?.resourceData || response || [];

const getTrackerId = (item: any) =>
    item?.trackerId || item?.cohortParticipantId || item?.trackerid || item?.id;

const getLearningPathId = (item: any) =>
    item?.learningPathId || item?.journeyId || item?.id;

const getProgressPercent = (item: any) => {
    const total = item?.totalCourses || item?.numberOfCourses || 0;
    const completed = item?.completedCourses || item?.numberOfCompletedCourses || 0;
    if (!total) return 0;
    return Math.round((completed / total) * 100);
};

const weeklyActivity = [60, 40, 70, 55, 85, 50, 30];
const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const { data: profileRes } = useQuery(
        ["participant-profile"],
        participantApi.getProfile,
        { staleTime: 60000 }
    );

    const { data: learningPathsRes, isLoading: learningPathsLoading } = useQuery(
        ["participant-learning-paths-dashboard"],
        participantApi.getLearningPaths,
        { staleTime: 60000 }
    );

    const profile = profileRes?.data || profileRes || {};
    const learningPaths = Array.isArray(getCollection(learningPathsRes))
        ? getCollection(learningPathsRes)
        : [];
    const topLearningPath =
        learningPaths.find((item: any) => item?.status === "in_progress") || learningPaths[0];

    const learningPathCount = learningPaths.length;
    const assignedCourseCount = learningPaths.reduce(
        (acc: number, item: any) =>
            acc +
            (item?.totalCourses ||
                item?.numberOfCourses ||
                item?.coursesCount ||
                item?.courses?.length ||
                item?.numberOfAssignedCourses ||
                0),
        0
    );
    const completedCourseCount = learningPaths.reduce(
        (acc: number, item: any) =>
            acc +
            (item?.completedCourses || item?.numberOfCompletedCourses || 0),
        0
    );

    const dynamicLearningData = [
        {
            title: "Learning Paths",
            value: learningPathCount,
            note: `${learningPaths.filter((p: any) => (p?.status || "").toLowerCase() === "in_progress").length} in progress`,
        },
        {
            title: "Assigned Courses",
            value: assignedCourseCount,
            note: `${completedCourseCount} completed`,
        },
        {
            title: "Certificates",
            value: profile?.numberOfCertificates || 0,
            note: "Certificates issued",
        },
    ];

    const openTopLearningPath = () => {
        if (!topLearningPath) return;
        const trackerId = getTrackerId(topLearningPath);
        const learningPathId = getLearningPathId(topLearningPath);
        if (!trackerId || !learningPathId) return;
        navigate(`/participant/courses-and-learning-path/${trackerId}/${learningPathId}`);
    };

    const name = user?.name || user?.displayName || "Participant";
    const displayName = profile?.name || profile?.displayName || name;
    const topPathProgress = topLearningPath ? getProgressPercent(topLearningPath) : 0;

    return (
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                    Hello, <span className="capitalize">{displayName}</span>
                </h1>

                <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <p className="text-xs text-gray-500">Primary Email</p>
                    <p className="text-sm text-gray-700 mt-1 break-all">{profile?.email || profile?.userPrincipalName || "Not available"}</p>
                </div>

                <div className="mt-4 space-y-3">
                    {dynamicLearningData.map((item) => (
                        <div key={item.title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                            <p className="text-xs text-gray-500">{item.title}</p>
                            <div className="mt-2 flex items-end justify-between">
                                <p className="text-2xl font-semibold text-[#0A3059]">{item.value}</p>
                                <p className="text-xs text-gray-500">{item.note}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <p className="text-sm font-medium text-gray-700 mb-4">Activity this week</p>
                    <div className="grid grid-cols-7 gap-2 items-end h-[140px]">
                        {weeklyActivity.map((value, index) => (
                            <div key={days[index]} className="flex flex-col items-center justify-end gap-2">
                                <div className="w-full bg-gray-100 rounded-full h-[110px] flex items-end overflow-hidden">
                                    <div className="w-full bg-[#0A3059] rounded-full" style={{ height: `${value}%` }} />
                                </div>
                                <span className="text-[10px] text-gray-500">{days[index]}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <p className="text-sm font-medium text-gray-700">Top Learning Path</p>
                    <div className="mt-3 space-y-3">
                        {learningPathsLoading ? (
                            <div className="h-[80px] flex items-center justify-center">
                                <ScaleLoader height={18} width={3} radius={2} color={"#FF9100"} />
                            </div>
                        ) : topLearningPath ? (
                            <>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Title</span>
                                    <span className="font-medium text-gray-800">{topLearningPath?.learningPathName || "Learning Path"}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Progress</span>
                                    <span className="font-medium text-gray-800">{topPathProgress}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="h-2 bg-[#8CB33E]" style={{ width: `${topPathProgress}%` }} />
                                </div>
                                <button
                                    type="button"
                                    onClick={openTopLearningPath}
                                    className="px-4 py-2 rounded-md text-white bg-[#0A3059] hover:bg-[#082649] text-xs"
                                >
                                    View Learning Path
                                </button>
                            </>
                        ) : (
                            <p className="text-sm text-gray-500">No learning path assigned yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;